// The first draft of a new metric's forecast (the "New metric" dialog). The person gives a range and two dates,
// and the dialog draws a starting line for them to drag. This only chooses where the handles start: it is input
// to the forecast, like a default value, not a result. The API's engine computes everything after "Create".

import { addMonths, toAbs } from "@/lib/months";
import type { Month } from "@/model/types";

export type SketchPoint = { month: Month; v: number };

/** Handle months from start to end: about one a quarter, at least 3 and at most 7. */
export function sketchMonths(start: Month, end: Month): Month[] {
	const span = toAbs(end) - toAbs(start);
	if (span < 1) return [start];
	const count = Math.min(span + 1, Math.max(3, Math.min(7, Math.round(span / 3) + 1)));
	const out: Month[] = [];
	for (let i = 0; i < count; i++) {
		const m = addMonths(start, Math.round((i * span) / (count - 1)));
		if (out.at(-1) !== m) out.push(m);
	}
	return out;
}

/**
 * A random walk from the middle of [lo, hi]. Each step moves up to 10% of the range, and the walk stays in the
 * middle 60% so that every handle has room to move both ways.
 */
export function sketchWalk(months: Month[], lo: number, hi: number, rand: () => number = Math.random): SketchPoint[] {
	const span = hi - lo;
	const mid = lo + span / 2;
	let v = mid;
	return months.map((month, i) => {
		if (i > 0) v = Math.max(lo + span * 0.2, Math.min(hi - span * 0.2, v + (rand() * 2 - 1) * span * 0.1));
		return { month, v: tidy(v, span) };
	});
}

/**
 * Low / Likely / High at each handle. The band starts narrow and gets wider with time, and stays inside the range
 * the person gave. They can change it on the metric page.
 */
export function sketchRange(points: SketchPoint[], lo: number, hi: number): Record<Month, [number, number, number]> {
	const span = hi - lo;
	const n = points.length;
	return Object.fromEntries(
		points.map(({ month, v }, i) => {
			const half = span * (0.04 + (0.12 * i) / Math.max(1, n - 1));
			return [month, [tidy(Math.max(lo, v - half), span), v, tidy(Math.min(hi, v + half), span)]];
		}),
	);
}

/** Round to a step that is small next to the range, so saved values read cleanly ("2.35M", not "2.3471129M"). */
export function tidy(v: number, span: number): number {
	if (!(span > 0)) return v;
	const step = Math.pow(10, Math.floor(Math.log10(span)) - 2);
	return Number((Math.round(v / step) * step).toPrecision(12));
}
